/**
 * useKeyboardShortcuts Hook
 * Handles keyboard shortcuts for comment actions and navigation.
 */
import { useEffect, useCallback } from 'react';
import type { Comment } from '../types';

interface KeyboardHandlers {
  accept: () => void;
  edit: () => void;
  reject: () => void;
  nextComment: () => void;
  prevComment: () => void;
}

interface UseKeyboardShortcutsOptions {
  /**
   * Currently selected/active comment
   */
  activeComment: Comment | null;

  /**
   * Handler functions for keyboard actions
   */
  handlers: KeyboardHandlers;

  /**
   * Whether shortcuts are enabled (default: true)
   */
  enabled?: boolean;

  /**
   * Whether a modal is open (disables most shortcuts)
   */
  modalOpen?: boolean;
}

/**
 * Hook to handle keyboard shortcuts for the grading interface.
 *
 * Shortcuts:
 * - `a`: Accept current comment
 * - `e`: Edit current comment
 * - `r`: Reject current comment
 * - `j` / `ArrowDown`: Next comment
 * - `k` / `ArrowUp`: Previous comment
 * - `Escape`: Cancel/close (handled by modals directly)
 */
export function useKeyboardShortcuts({
  activeComment,
  handlers,
  enabled = true,
  modalOpen = false,
}: UseKeyboardShortcutsOptions): void {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Don't trigger shortcuts when disabled
      if (!enabled) return;

      // Don't trigger shortcuts when typing in input/textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Don't trigger action shortcuts when modal is open
      // (allow navigation shortcuts though)
      const isModalOpen = modalOpen;

      // Navigation shortcuts (always available)
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        handlers.nextComment();
        return;
      }

      if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        handlers.prevComment();
        return;
      }

      // Action shortcuts (require active comment and no modal)
      if (!activeComment || isModalOpen) return;

      switch (e.key.toLowerCase()) {
        case 'a':
          e.preventDefault();
          handlers.accept();
          break;

        case 'e':
          e.preventDefault();
          handlers.edit();
          break;

        case 'r':
          e.preventDefault();
          handlers.reject();
          break;
      }
    },
    [activeComment, handlers, enabled, modalOpen]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

export default useKeyboardShortcuts;
